import React, { useState, useEffect, useMemo } from 'react';
import AdminSidebar from '../../components/ui/AdminSidebar';
import AdminDownbar from '../../components/ui/AdminDownbar';
import Icon from '../../components/AppIcon';
import Modal from '../../components/Modal';
import Input from '../../components/ui/Input';
import { useCatalog } from '../../hooks/useCatalog';
import { useCategories } from '../../hooks/useCategories';
import { useBibles } from '../../hooks/useBibles';
import { useThings } from '../../hooks/useThings';
import BookForm from '../admin-catalog-management-screen/components/BookForm';
import BibleForm from '../admin-catalog-management-screen/components/BibleForm';
import ThingForm from '../admin-catalog-management-screen/components/ThingForm';
import ProductTypeModal from '../admin-catalog-management-screen/components/ProductTypeModal';
import BookGrid from '../../../src/pages/admin-catalog-management-screen/components/BookGrid';

const AdminCatalogManagementScreen = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'
  const [currentProductType, setCurrentProductType] = useState('book'); // 'book', 'bible', 'thing'
  const [searchQuery, setSearchQuery] = useState('');
  
  const { catalog: allProducts, loading, error, addBook, updateBook, deleteBook } = useCatalog('admin');
  const { categories } = useCategories();
  const { 
    bibles: biblesData, 
    addBible: addBibleHook, 
    updateBible: updateBibleHook, 
    deleteBible: deleteBibleHook 
  } = useBibles();
  const { 
    things: thingsData, 
    addThing: addThingHook, 
    updateThing: updateThingHook, 
    deleteThing: deleteThingHook 
  } = useThings();

  // Separate products by type and add category names
  const books = allProducts.filter(product => product.product_type === 'book').map(book => ({
    ...book,
    productType: 'book',
    id: book.id_product,
    categoryNames: book.categories_product?.map(catId => 
      categories.find(cat => cat.id_category === catId)?.name_category
    ).filter(Boolean) || []
  }));
  
  
  const bibles = biblesData.map(bible => ({
    ...bible,
    productType: 'bible',
    id: bible.bible_id,
    // Mapear campos para consistencia con el sistema de filtros
    title: bible.title_bible,
    author: bible.version_bible,
    description: bible.description_bible,
    price: bible.price_bible,
    available: bible.available_bible,
    categoryNames: [] // Las biblias no usan categorías como los libros
  }));
  
  
  const things = thingsData.map(thing => ({
    ...thing,
    productType: 'thing',
    id: thing.thing_id,
    // Mapear campos para consistencia
    title: thing.title_thing,
    author: thing.type_thing || 'Sin tipo', // Usar el tipo dinámico del producto
    description: thing.description_thing,
    price: thing.price_thing,
    available: thing.available_thing,
    categoryNames: thing.categories_thing?.map(catId => 
      categories.find(cat => cat.id_category === catId)?.name_category
    ).filter(Boolean) || []
  }));

  // Combine all products with proper mapping
  const processedProducts = [...books, ...bibles, ...things];
  

  // Filtrar productos por búsqueda
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return processedProducts;
    
    const query = searchQuery.toLowerCase();
    return processedProducts.filter(product => {
      // Handle different product types
      let title = '';
      let author = '';
      let description = '';
      
      if (product.productType === 'book') {
        title = product.title_product?.toLowerCase() || '';
        author = product.authorsbook?.name_author?.toLowerCase() || product.authorName?.toLowerCase() || '';
        description = product.description_product?.toLowerCase() || '';
      } else if (product.productType === 'bible') {
        title = product.title?.toLowerCase() || '';
        author = product.author?.toLowerCase() || '';
        description = product.description?.toLowerCase() || '';
      } else if (product.productType === 'thing') {
        title = product.title?.toLowerCase() || '';
        author = product.author?.toLowerCase() || '';
        description = product.description?.toLowerCase() || '';
      }
      
      return title.includes(query) || author.includes(query) || description.includes(query);
    });
  }, [processedProducts, searchQuery]);

  // Función para resaltar términos de búsqueda
  const highlightSearchTerm = (text, searchTerm) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className="bg-orange-200 text-orange-900 px-1 rounded font-medium">
          {part}
        </span>
      ) : part
    );
  };



  // Manejar edición de cualquier producto
  const handleEditItem = (item) => {
    setEditingItem(item);
    setCurrentProductType(item.productType);
    setFormMode('edit');
    setIsModalOpen(true);
  };

  // Manejar creación de nuevo producto
  const handleCreateProduct = () => {
    setIsTypeModalOpen(true);
  };

  // Manejar selección de tipo de producto
  const handleSelectProductType = (type) => {
    setCurrentProductType(type);
    setEditingItem(null);
    setFormMode('create');
    setIsModalOpen(true);
  };

  // Manejar guardado (crear o actualizar)
  const handleSaveItem = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setFormMode('create');
    setCurrentProductType('book');
  };

  // Manejar cancelación
  const handleCancelEdit = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setFormMode('create');
    setCurrentProductType('book');
  };


  // Function to convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Function to handle image selection
  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    const base64Images = [];
    
    // Check if adding new images would exceed the limit
    if (selectedImages.length + files.length > 5) {
      alert('Máximo 5 imágenes permitidas');
      return;
    }
    
    for (const file of files) {
      try {
        const base64 = await convertToBase64(file);
        base64Images.push(base64);
      } catch (error) {
        console.error('Error converting image to base64:', error);
      }
    }
    
    setSelectedImages(prev => [...prev, ...base64Images]);
  };

  // Function to remove image
  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  // Detect if device is mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;

    const title = form.title.value.trim();
    const price = parseFloat(form.price.value);
    const available = form.available.value === 'true';
    const ranking = parseFloat(form.ranking.value);
    const author_id = form.author_id.value;
    const category_id = form.category_id.value;
    const language = form.language.value;
    const description = form.description.value.trim();
    const specs = form.specs.value.trim();
    const pages = parseInt(form.pages.value);
    const publisher = form.publisher.value.trim();
    const format = form.format.value.trim();
    const featured_points = form.featured_points.value.trim();
    
    if (!title || isNaN(price) || !author_id || !category_id || !language) {
      alert('Por favor completa todos los campos obligatorios.');
      return;
    }

    // Parse specs as JSON if provided
    let specsObject = null;
    if (specs) {
      try {
        specsObject = JSON.parse(specs);
      } catch (error) {
        alert('Las especificaciones deben estar en formato JSON válido.');
        return;
      }
    }

    // Parse featured points as array
    const featuredPointsArray = featured_points 
      ? featured_points.split('\n').filter(point => point.trim())
      : [];

    const data = {
      title_product: title,
      price_original_product: price,
      available_product: available,
      rating_product: ranking,
      author_id: parseInt(author_id),
      categories_product: category_id ? [parseInt(category_id)] : [],
      language_product: language,
      description_product: description,
      specs_product: specsObject,
      pages_product: pages || null,
      featured_point_product: featuredPointsArray,
      image_url_product: selectedImages,
    };

    try {
      if (editingProduct) { // If editing an existing product
        await updateProduct(editingProduct.id_product, data);
      } else {
        await createProduct(data);
      }
      await fetchBooks();
      setIsModalOpen(false);
      setEditingProduct(null);
      setSelectedImages([]);
    } catch (err) {
      console.error('Error submitting form:', err);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-[120px] lg:pb-0">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <AdminSidebar
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          userName="Administrador"
        />
      </div>
      {/* Mobile Bottom Bar */}
      <div className="lg:hidden">
        <AdminDownbar />
      </div>
      <div className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <header
          className="sticky top-0 z-50 p-6 bg-white/80 border-b border-slate-200 backdrop-blur-lg"
          style={{ backgroundColor: '#fbfbfb' }}
        >
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-md bg-[#c375461a]">
                    <Icon name="BookOpenText" size={24} className="text-[#c37546]" />
                  </div>
                  <h1 className="text-2xl font-heading font-bold text-card-foreground">
                    Gestión del Catálogo
                  </h1>
                </div>
                <p className="text-sm italic text-card-foreground mt-2">
                  “Instruye al niño en su camino, y aun cuando fuere viejo no se apartará de él.” — Proverbios 22:6
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  onClick={handleCreateProduct}
                >
                  <Icon name="Plus" size={16} />
                  <span className="font-medium">Nuevo producto</span>
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Search Bar Above Products */}
            <div className="px-4 sm:px-6 lg:px-0">
              <div className="relative w-full">
                <Input
                  type="search"
                  placeholder="Buscar productos por título, autor o descripción..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 w-full"
                />
                <Icon 
                  name="Search" 
                  size={18} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
                />
              </div>
            </div>
            {loading ? ( // Loading indicator
              <div className="flex items-center justify-center py-12">
                <div className="flex items-center space-x-3">
                  <Icon name="Loader2" size={24} className="animate-spin text-primary" />
                  <span className="text-muted-foreground">Cargando catálogo...</span>
                </div>
              </div>
            ) : (
              <>
                {error && (
                  <div className="mb-4 text-red-600">
                    {error}
                  </div>
                )}
                {/* Contador de productos */}
                <div className="px-4 sm:px-6 lg:px-0 mb-4">
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span>{books.length} Libros</span>
                    <span>{bibles.length} Biblias</span>
                    <span>{things.length} Otros</span>
                    <span className="font-medium">Total: {processedProducts.length} productos</span>
                    {searchQuery && (
                      <span className="text-primary font-medium">Mostrando: {filteredProducts.length} resultados</span>
                    )}
                  </div>
                </div>
                
                {/* Grid de productos */}
                <div className="px-4 sm:px-6 lg:px-0">
                  {filteredProducts.length > 0 ? (
                    <BookGrid 
                      books={filteredProducts} 
                      onEditBook={handleEditItem} 
                      searchQuery={searchQuery}
                      highlightSearchTerm={highlightSearchTerm}
                    />
                  ) : searchQuery ? (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground">No se encontraron productos que coincidan con "{searchQuery}"</p>
                    </div>
                  ) : processedProducts.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground">No hay productos en el catálogo</p>
                    </div>
                  ) : null}
                </div>
              </>
            )}
          </div>
        </main>
      </div>
      {/* Modal de selección de tipo de producto */}
      <ProductTypeModal
        isOpen={isTypeModalOpen}
        onClose={() => setIsTypeModalOpen(false)}
        onSelectType={handleSelectProductType}
      />

      {/* Modal de formulario según el tipo de producto */}
      {isModalOpen && (
        <Modal onClose={handleCancelEdit}>
          {currentProductType === 'book' && (
            <BookForm
              mode={formMode}
              editingBook={editingItem}
              onSave={handleSaveItem}
              onCancel={handleCancelEdit}
              addBook={addBook}
              updateBook={updateBook}
              deleteBook={deleteBook}
            />
          )}
          {currentProductType === 'bible' && (
            <BibleForm
              mode={formMode}
              editingBible={editingItem}
              onSave={handleSaveItem}
              onCancel={handleCancelEdit}
              addBible={addBibleHook}
              updateBible={updateBibleHook}
              deleteBible={deleteBibleHook}
            />
          )}
          {currentProductType === 'thing' && (
            <ThingForm
              mode={formMode}
              editingThing={editingItem}
              onSave={handleSaveItem}
              onCancel={handleCancelEdit}
              addThing={addThingHook}
              updateThing={updateThingHook}
              deleteThing={deleteThingHook}
            />
          )}
        </Modal>
      )}

    </div>
  );
}
export default AdminCatalogManagementScreen;
